import { Action } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dispatch } from 'redux';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { registerAnalyticsEventSendInn } from '../analytics';
import { Error } from '../components/error/error';
import { VedleggError } from '../components/error/vedleggError';
import {
    OppsummeringTabell,
    saveButtonProps,
    UtleggLinje,
    ViewBesvartSporsmal,
} from '../components/oppsummering/oppsummering';
import { OppsummeringFooter } from '../components/oppsummering/oppsummering-footer';
import { StepNavigation } from '../components/step-navigation/step-navigation';
import { useBankregnr } from '../config';
import { useKunde } from '../kundeinfo/kunde-context';
import { getExternalUrlForSkadetype } from '../landingsside/deep-link-handler';
import { logger } from '../logg/logger';
import { AppState } from '../reducers';
import { SavingState } from '../reducers/savingState';
import { urler } from '../router/router-urler';
import { isSkadedatoSporsmal } from '../sporsmalsflyt/dato/dato-types';
import { findChosenForsikringFromSporsmal } from '../sporsmalsflyt/forsikring/forsikring-types';
import { filterForsikringerBySkadeDatoAndPrioritization } from '../sporsmalsflyt/forsikring/forsikring-utils';
import { useWindowScrollTop } from '../sporsmalsflyt/hooks';
import { CurrentKontaktinfosporsmal, isKontaktinfoSporsmal } from '../sporsmalsflyt/kontaktinfo/kontaktinfo-types';
import { BesvartSporsmal, flattenBesvarteSporsmal } from '../sporsmalsflyt/reducer/current-steg';
import { previousStepAction } from '../sporsmalsflyt/reducer/sporsmalsflyt-actions';
import {
    BesvartUtleggsporsmal,
    isUtleggSporsmal,
    utleggslinjeContainsInformation,
} from '../sporsmalsflyt/utlegg/utlegg-types';
import { ForsikringDekningDTO, MeldSkadeResultWebDTO, SporsmalsflytSkademeldingDTO } from '../types/generated-types';
import { ForsikringUnion, Kundeinfo, SkademeldingError } from '../types/internal-types';
import { SideType } from '../utils/piwik-utils';
import { KontaktinfoOppsummering } from './kontaktinfo-oppsummering';
import { Motorvogninformasjon } from './motorvogn/motorvogninformasjon';
import {
    getVedlegg,
    resetSavingState,
    saveSkademelding,
    saveSkademeldingBadRequestFailure,
    saveSkademeldingFailure,
    saveSkademeldingSuccess,
    saveSkademeldingUnauthorisedFailure,
    saveSkademeldingVedleggFailure,
    sendSkademeldingApi,
    sporsmalsflytskademeldingDTOFrom,
    VedleggSource,
} from './reducer/skademelding-actions';
import { updateSkademeldingForsikringOppsummeringResetSavingState } from './reducer/skademeldingOppsummering-actions';
import { skadetypeToString } from './reducer/skadetype';

function findChosenForsikring(
    flattenedBesvarteSporsmal: BesvartSporsmal[],
    kunde?: Kundeinfo,
): ForsikringUnion | undefined {
    if (!kunde) {
        return undefined;
    }

    const selectedForsikringAlternative = findChosenForsikringFromSporsmal(flattenedBesvarteSporsmal);
    const skadedatoSporsmal = flattenedBesvarteSporsmal.find(isSkadedatoSporsmal);

    if (!selectedForsikringAlternative || !skadedatoSporsmal) {
        return undefined;
    }

    const chosenForsikring: ForsikringUnion[] = filterForsikringerBySkadeDatoAndPrioritization(
        kunde.forsikringer || [],
        new Date(skadedatoSporsmal.dato),
        undefined,
    ).filter(
        (forsikring: ForsikringUnion) =>
            forsikring.forsikringsnummer === selectedForsikringAlternative.forsikringsnummer,
    );

    return chosenForsikring ? chosenForsikring[0] : undefined;
}

export const SkademeldingOppsummering = () => {
    const state = useSelector((state: AppState) => state);
    const { skadetype } = state.skademelding;
    const windowScrolltop = useWindowScrollTop();
    const navigate = useNavigate();
    const dispatch: Dispatch<Action> = useDispatch();
    const bankregnr = useBankregnr();
    const kunde = useKunde();

    const {
        savingState,
        sporsmalsflytState: { besvarteSporsmal },
    } = useSelector((state: AppState) => state.skademelding);

    const savingStateKjoretoyOppsummering = useSelector(
        (state: AppState) => state.skademeldingOppsummering.savingState,
    );

    useEffect(() => {
        const kvitteringUrl = `${urler().sporsmalsflytKvitteringUrl}?skadetype=${skadetypeToString(skadetype)}`;

        if (savingState === SavingState.Success) {
            navigate(kvitteringUrl);
        }
    }, [savingState, dispatch, navigate, skadetype]);

    useEffect(() => {
        return () => {
            dispatch(resetSavingState());
            dispatch(updateSkademeldingForsikringOppsummeringResetSavingState());
        };
    }, [dispatch]);

    const flattenedBesvarteSporsmal = flattenBesvarteSporsmal(besvarteSporsmal);

    const kontaktinfo: CurrentKontaktinfosporsmal | undefined = flattenedBesvarteSporsmal.find(isKontaktinfoSporsmal);

    const utleggSporsmal: BesvartUtleggsporsmal | undefined = flattenedBesvarteSporsmal.find(isUtleggSporsmal);

    const selectedForsikring: ForsikringUnion = findChosenForsikring(flattenedBesvarteSporsmal, kunde.kundeinfo);

    const isKjoretoyKaskoOrDelkasko = () => {
        switch (selectedForsikring.dekning) {
            case ForsikringDekningDTO.KjoretoyAnsvar:
            case ForsikringDekningDTO.KjoretoyKasko:
            case ForsikringDekningDTO.KjoretoyKaskoLeiebil:
            case ForsikringDekningDTO.KjoretoyKaskoPluss:
            case ForsikringDekningDTO.KjoretoyDelkasko:
            case ForsikringDekningDTO.BilKaskoPluss:
            case ForsikringDekningDTO.BilKaskoLeiebil:
                return true;

            default:
                return false;
        }
    };

    const showTilInformasjon = selectedForsikring && isKjoretoyKaskoOrDelkasko();

    const sendSkademelding = (bankregnr: string) => {
        const skademelding: SporsmalsflytSkademeldingDTO = sporsmalsflytskademeldingDTOFrom(
            state.skademelding.skadetype,
            state.skademelding.sporsmalsflytState.besvarteSporsmal,
            state.skademeldingOppsummering.forsikringOppsummeringInformation,
            getVedlegg(state, VedleggSource.Skademelding),
        );

        dispatch(saveSkademelding());

        sendSkademeldingApi(skademelding, bankregnr)
            .then((meldSkadeResultWebDTO: MeldSkadeResultWebDTO) =>
                dispatch(saveSkademeldingSuccess(meldSkadeResultWebDTO)),
            )
            .catch((exception: RequestExceptionWithResponse) => {
                try {
                    const log = logger(bankregnr);
                    log.warn(
                        `Kunde was unable to send skade from Oppsummering-side.`,
                        flattenBesvarteSporsmal(state.skademelding.sporsmalsflytState.besvarteSporsmal).find(
                            isKontaktinfoSporsmal,
                        )?.fodselsnummer ?? 'ingen kundeidentifikator',
                        state.skademelding.skadetype,
                        exception,
                    );
                    // eslint-disable-next-line no-empty
                } catch {}
                if (exception.errorName === SkademeldingError.vedleggError) {
                    dispatch(saveSkademeldingVedleggFailure());
                } else if (exception.status.valueOf() === 401) {
                    dispatch(saveSkademeldingUnauthorisedFailure());
                } else if (exception.status.valueOf() >= 400 && exception.status.valueOf() < 500) {
                    dispatch(saveSkademeldingBadRequestFailure());
                } else {
                    dispatch(saveSkademeldingFailure());
                }
            });
    };

    const submit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        registerAnalyticsEventSendInn(skadetype);
        sendSkademelding(bankregnr);
    };

    if (savingState === SavingState.Success) {
        return null;
    }

    return (
        <div className="container__standard-width oppsummering">
            <h2 className="oppsummering-title">Se over opplysningene før du sender inn:</h2>
            {showTilInformasjon && (
                <Motorvogninformasjon
                    bankregnr={bankregnr}
                    selectedForsikring={selectedForsikring}
                    flattenedBesvarteSporsmal={flattenedBesvarteSporsmal}
                    skadetype={skadetype}
                />
            )}
            <OppsummeringTabell header="Om skaden">
                {besvarteSporsmal.map((besvartSporsmal) => (
                    <ViewBesvartSporsmal besvartSporsmal={besvartSporsmal} key={besvartSporsmal.sporsmal.id} />
                ))}
            </OppsummeringTabell>

            {utleggSporsmal && utleggSporsmal.utleggslinjer.filter(utleggslinjeContainsInformation).length > 0 && (
                <OppsummeringTabell header="Utlegg">
                    {utleggSporsmal.utleggslinjer.map((linje) => (
                        <UtleggLinje linje={linje} key={linje.id} />
                    ))}
                </OppsummeringTabell>
            )}

            {kontaktinfo && <KontaktinfoOppsummering kontaktinfo={kontaktinfo} />}

            <OppsummeringFooter />

            {savingState === SavingState.UgyldigVedlegg && <VedleggError />}

            {savingState === SavingState.Failure && (
                <Error
                    headerText="Her gikk noe galt – prøv igjen!"
                    bodyText="Skademeldingen ble ikke sendt inn, prøv igjen. Dersom problemet gjentar seg flere ganger, kan du ringe oss på tlf 915&nbsp;03&nbsp;850."
                />
            )}

            {savingState === SavingState.UnauthorizedUser && (
                <Error
                    headerText="Du er ikke logget inn"
                    bodyText="Skademeldingen ble ikke sendt inn. Logg inn for å sende skademeldingen på nytt. Vi har tatt vare på utfyllingen din."
                    errorButtonProps={{
                        buttonText: 'Logg inn',
                        buttonDataTrackId: 'error-button-logg-inn',
                        onButtonClick: () => window.location.replace(getExternalUrlForSkadetype(skadetype)),
                        sideDetail: { type: SideType.Oppsummeringsside, skademeldingstype: skadetype },
                    }}
                />
            )}

            {savingState === SavingState.BadRequestFailure && (
                <Error
                    headerText="Her gikk noe galt"
                    bodyText="Skademeldingen ble ikke sendt inn. Vi anbefaler at du logger ut og prøver igjen. Dersom problemet gjentar seg flere ganger, kan du ringe oss på tlf 915&nbsp;03&nbsp;850."
                />
            )}

            <StepNavigation
                bankregnr={bankregnr}
                prev={{
                    onClick: () => {
                        windowScrolltop();
                        dispatch(previousStepAction());
                    },
                }}
                next={saveButtonProps(savingState, submit, savingStateKjoretoyOppsummering)}
                isOppsummering
                sideDetail={{ type: SideType.Oppsummeringsside, skademeldingstype: skadetype }}
            />
        </div>
    );
};
